/**
 * PSP/CRE TOOL v2.0 - DAMAGE CONTROL SURGERY & PROLONGED FIELD CARE
 * 
 * @fileoverview Damage Control Surgery (DCS) protocols, Prolonged Field Care (PFC),
 *               blood product consumption, and facility capability specifications
 * @module constants/damageControlSurgery
 * @author Dr. Tatar Radu MD - Romanian Military Medical Institute
 * @date December 20, 2024
 * @version 2.0 - Enhanced with REBOA, lethal triad algorithm, DCS scoring
 * 
 * Changes from v1.0:
 * - Added REBOA_PROTOCOL for hemorrhage control
 * - Added LETHAL_TRIAD_ALGORITHM with scoring
 * - Added DCS_INDICATION_SCORE calculator
 * - Added TEMPERATURE_MANAGEMENT protocols
 * - Added UKRAINE_SURGICAL_STATISTICS
 * - Added specialty-specific DCS protocols
 * - Enhanced calculator integration functions
 * 
 * DATA SOURCES:
 * - CASUALTY_CALCULATOR_ARCHITECTURE_PART2_NOV24_2025.md (lines 791-796)
 * - MODERN_WARFARE_CASUALTY_PATTERNS_2024.md (lines 541-566)
 * - Ukraine 2022-2024 operational data (DCS and PFC usage rates)
 * - US Army ATP 4-02.55 (Medical Planning)
 * 
 * PURPOSE:
 * Educational tool for 19-year-old Romanian military medical students
 * Learning modern trauma surgery principles, resource-driven medical decisions
 * 
 * CRITICAL CONCEPTS FOR STUDENTS:
 * 1. DCS is "abbreviated surgery" - stop bleeding, close fast, resuscitate
 * 2. Definitive repair comes LATER (48-96h) after patient stabilized
 * 3. PFC is NOT failure - it's planned capability for contested environments
 * 4. Blood product consumption drives surgical capacity constraints
 * 5. Role 2 Enhanced can do Phase 1 DCS, Role 3 can do all phases
 * 
 * UKRAINIAN REALITY:
 * In contested environments, PFC required for 35-65% of casualties due to
 * evacuation delays. DCS principles saved lives when immediate evacuation
 * impossible. This is the NEW NORMAL for peer warfare, not rare exception.
 */

// ============================================================================
// DAMAGE CONTROL SURGERY (DCS) - THREE PHASES
// ============================================================================

/**
 * DCS Phase definitions with time requirements and resource consumption
 * 
 * EDUCATIONAL NOTE:
 * Traditional surgery focuses on DEFINITIVE repair in one operation.
 * DCS accepts that patient too unstable for long operation - do MINIMUM
 * to save life, then resuscitate, THEN complete repair when stable.
 * 
 * This is philosophically different: "Life over limb, limb over function"
 * 
 * ROMANIAN MILITARY CONTEXT:
 * Role 2 Enhanced facilities can perform Phase 1 DCS (abbreviated surgery).
 * Role 3 facilities can perform all phases including definitive repair.
 * Understanding these limitations critical for medical planning.
 */
export const DCS_PHASES = {
  
  /**
   * PHASE 1 - ABBREVIATED SURGERY (30-90 minutes)
   * "Damage Control" - Stop bleeding, control contamination, temporary closure
   * 
   * GOAL: Patient survives transport to ICU
   * NOT GOAL: Definitive repair (comes later)
   * 
   * PROCEDURES:
   * - Pack bleeding: Don't waste time dissecting vessels, PACK with gauze
   * - Control contamination: Quick bowel repair/resection, NOT anastomosis
   * - Temporary abdominal closure: Vacuum dressing, NOT fascial closure
   * - External fixation: Stabilize fractures, NOT internal fixation
   * 
   * UKRAINIAN EXPERIENCE:
   * Phase 1 DCS at Role 2 Enhanced saved lives when Role 3 evacuation delayed.
   * Surgeons trained to resist urge to "fix everything" - pack and close FAST.
   */
  PHASE_1_ABBREVIATED_SURGERY: {
    name: 'Abbreviated Surgery',
    duration_minutes_min: 30,        // Fastest possible (simple packing)
    duration_minutes_typical: 60,    // Most common duration
    duration_minutes_max: 90,        // Complex multi-cavity trauma
    
    procedures: {
      thoracic: {
        operations: [
          'Emergency thoracotomy',
          'Lung tractotomy (staple bleeding vessels)',
          'Cardiac repair (if salvageable)',
          'Temporary chest closure'
        ],
        blood_loss_typical_ml: 2500,
        duration_minutes: 45
      },
      
      abdominal: {
        operations: [
          'Exploratory laparotomy',
          'Pack all four quadrants',
          'Damage control bowel resection (NO anastomosis)',
          'Damage control hepatic packing',
          'Temporary abdominal closure (VAC or Bogota bag)'
        ],
        blood_loss_typical_ml: 3000,
        duration_minutes: 60
      },
      
      vascular: {
        operations: [
          'Proximal/distal control',
          'Shunt placement (NOT definitive repair)',
          'Ligation if limb not salvageable'
        ],
        blood_loss_typical_ml: 2000,
        duration_minutes: 40
      },
      
      orthopedic: {
        operations: [
          'External fixation (NOT internal)',
          'Amputation if unsalvageable',
          'Wound debridement (gross only)'
        ],
        blood_loss_typical_ml: 1500,
        duration_minutes: 35
      }
    },
    
    // Critical resource consumption during Phase 1
    resources_consumed: {
      blood_products: {
        prbc_units_typical: 12,      // Packed Red Blood Cells
        ffp_units_typical: 12,       // Fresh Frozen Plasma (1:1 ratio)
        platelet_units_typical: 2,   // Platelet packs
        cryoprecipitate: 10,         // Fibrinogen replacement
        total_units_typical: 36,     // Massive transfusion
        
        // Extreme cases (vascular injury, multiple cavities)
        massive_transfusion_threshold: 10,  // >10 units = MTP
        extreme_consumption_units: 60       // Some patients need 60+ units
      },
      
      surgical_supplies: {
        laparotomy_pads: 20,         // For packing
        suction_canisters: 6,
        surgical_sponges: 50,
        hemostatic_agents: 3,        // Combat gauze, Celox, etc.
        chest_tubes: 2,
        vac_dressing: 1
      },
      
      team_requirements: {
        surgeon: 1,                  // Must be experienced in DCS
        assistant_surgeon: 1,
        anesthesiologist: 1,
        nurse_anesthetist: 1,
        scrub_tech: 1,
        circulating_nurse: 1,
        minimum_team_size: 6
      }
    },
    
    // Facility requirements
    facility_capabilities: {
      role2_basic_cannot_perform: true,      // No surgical capability
      role2_enhanced_can_perform: true,      // CAN do Phase 1 DCS
      role3_optimal: true,                   // Full capability
      role4_definitive_but_overkill: true    // Over-resourced for Phase 1
    },
    
    // Patient outcomes from Phase 1
    outcomes: {
      survival_to_icu_if_phase1_done: 0.85,      // 85% survive to ICU
      survival_if_attempted_definitive: 0.52,     // 52% if tried full repair
      mortality_benefit_phase1: 0.33,             // 33% mortality reduction
      
      complications: {
        hypothermia_risk: 0.65,              // 65% become hypothermic
        coagulopathy_risk: 0.58,             // 58% develop coagulopathy
        acidosis_risk: 0.72,                 // 72% develop acidosis
        lethal_triad_risk: 0.45              // 45% develop all three
      }
    },
    
    // Decision criteria for DCS vs definitive surgery
    indications_for_dcs: {
      hemodynamic_instability: 'SBP <90 mmHg despite resuscitation',
      hypothermia: 'Temperature <35Â°C',
      acidosis: 'pH <7.2',
      coagulopathy: 'INR >1.5 or clinical coagulopathy',
      massive_transfusion: '>10 units PRBC transfused',
      multi_cavity_trauma: 'Thoracic + abdominal injuries',
      prolonged_surgery_anticipated: '>2 hours if attempted definitively',
      resource_constraints: 'Multiple casualties waiting, limited blood'
    }
  },

  /**
   * PHASE 2 - ICU RESUSCITATION (24-72 hours)
   * "Damage Control Resuscitation" - Fix the lethal triad
   * 
   * GOAL: Correct hypothermia, acidosis, coagulopathy
   * THEN: Patient ready for definitive surgery
   * 
   * THE LETHAL TRIAD:
   * 1. Hypothermia (<35Â°C) â†’ impairs coagulation enzymes
   * 2. Acidosis (pH <7.2) â†’ impairs clotting factors
   * 3. Coagulopathy (INR >1.5) â†’ bleeding continues
   * 
   * Each worsens the others: "death spiral" unless corrected
   * 
   * UKRAINIAN EXPERIENCE:
   * Phase 2 duration depends on initial injury severity and Phase 1 speed.
   * Fastest patients ready for Phase 3 at 24h. Most need 48-72h. Some never
   * stabilize enough for Phase 3 (die in ICU or remain T4 expectant).
   */
  PHASE_2_ICU_RESUSCITATION: {
    name: 'ICU Resuscitation',
    duration_hours_min: 24,          // Fastest stabilization
    duration_hours_typical: 48,      // Most common
    duration_hours_max: 72,          // Maximum before attempting Phase 3
    
    // Goals that must be achieved before Phase 3
    resuscitation_goals: {
      temperature: '>36Â°C',
      ph: '>7.35',
      lactate: '<2 mmol/L',
      base_deficit: '>-2',
      inr: '<1.2',
      platelet_count: '>100,000',
      hemoglobin: '>10 g/dL',
      sbp: '>100 mmHg without pressors',
      urine_output: '>0.5 mL/kg/hr'
    },
    
    // Interventions during Phase 2
    interventions: {
      warming: {
        methods: [
          'Forced air warming blanket (Bair Hugger)',
          'Warmed IV fluids (40Â°C)',
          'Warmed blood products',
          'Warmed ventilator gases',
          'Peritoneal lavage with warm fluid (if abdomen open)'
        ],
        target: '>36Â°C',
        importance: 'CRITICAL - hypothermia kills via coagulopathy'
      },
      
      acid_base_correction: {
        methods: [
          'Adequate tissue perfusion (stop anaerobic metabolism)',
          'Mechanical ventilation optimization',
          'Bicarbonate if pH <7.1 (controversial)',
          'Correct underlying shock'
        ],
        target: 'pH >7.35',
        note: 'Do NOT give bicarb unless pH <7.1 AND patient ventilated'
      },
      
      coagulopathy_correction: {
        methods: [
          'FFP transfusion (target INR <1.2)',
          'Platelet transfusion (target >100k)',
          'Cryoprecipitate (fibrinogen replacement)',
          'Tranexamic acid if ongoing bleeding',
          'Recombinant Factor VIIa (last resort, expensive)'
        ],
        target: 'INR <1.2, platelets >100k',
        blood_consumption: '8-12 additional units FFP typical'
      },
      
      hemodynamic_support: {
        methods: [
          'Norepinephrine for vasopressor support',
          'PRBC transfusion to Hgb >10',
          'Avoid excessive crystalloid (worsens coagulopathy)',
          'Central venous pressure monitoring',
          'Arterial line for continuous BP monitoring'
        ],
        target: 'MAP >65 mmHg, CVP 8-12',
        note: 'Permissive hypotension ENDS after Phase 1 surgery'
      }
    },
    
    // Resource consumption during Phase 2
    resources_consumed: {
      blood_products_additional: {
        prbc_units: 8,               // Additional PRBC beyond Phase 1
        ffp_units: 12,               // Large FFP consumption for coagulopathy
        platelet_units: 2,
        cryoprecipitate: 5,
        total_additional_units: 27   // On top of Phase 1 consumption
      },
      
      icu_bed_days: 2,               // Typical 2 days in ICU
      
      personnel_requirements: {
        intensivist: 1,              // ICU physician
        icu_nurse_ratio: '1:1',      // One nurse per patient
        respiratory_therapist: 1,
        continuous_monitoring: true
      },
      
      equipment: {
        mechanical_ventilator: 1,
        continuous_monitoring: 1,
        warming_blanket: 1,
        blood_warmer: 1,
        infusion_pumps: 4
      }
    },
    
    // Facility requirements
    facility_capabilities: {
      role2_basic_cannot_provide: true,      // No ICU capability
      role2_enhanced_limited: true,          // Can stabilize 12-24h only
      role3_full_capability: true,           // Full ICU capability
      role4_optimal: true
    },
    
    // Outcomes from Phase 2
    outcomes: {
      survival_to_phase3: 0.78,              // 78% survive to definitive surgery
      mortality_in_icu: 0.22,                // 22% die during resuscitation
      
      causes_of_death: {
        persistent_coagulopathy: 0.35,       // 35% of ICU deaths
        multi_organ_failure: 0.30,           // 30% of ICU deaths
        sepsis: 0.20,                        // 20% of ICU deaths
        cardiac_arrest: 0.15                 // 15% of ICU deaths
      },
      
      time_to_stabilization: {
        within_24h: 0.30,                    // 30% ready at 24h
        within_48h: 0.55,                    // 55% ready at 48h
        within_72h: 0.78,                    // 78% ready at 72h
        never_stabilize: 0.22                // 22% die or remain unstable
      }
    }
  },

  /**
   * PHASE 3 - DEFINITIVE SURGERY (48-96 hours after Phase 1)
   * "Definitive Repair" - Complete the surgery properly
   * 
   * GOAL: Permanent anatomical repair
   * NOW SAFE: Patient warm, not acidotic, not coagulopathic
   * 
   * PROCEDURES:
   * - Remove packing, control bleeding definitively
   * - Complete bowel anastomosis
   * - Close abdomen permanently (if no compartment syndrome)
   * - Definitive vascular repair
   * - Skin grafting, wound closure
   * 
   * UKRAINIAN EXPERIENCE:
   * Phase 3 often delayed to 72-96h due to resource constraints. Patient
   * survival not affected by delaying Phase 3 to 96h IF Phase 2 goals met.
   * Some patients evacuated to Role 4 for Phase 3 (free up Role 3 beds).
   */
  PHASE_3_DEFINITIVE_SURGERY: {
    name: 'Definitive Surgery',
    timing_hours_after_phase1_min: 48,    // Earliest attempt
    timing_hours_after_phase1_typical: 72, // Most common
    timing_hours_after_phase1_max: 96,    // Can be delayed this long
    
    duration_minutes_typical: 180,        // 3 hours typical
    duration_minutes_complex: 360,        // 6 hours for complex cases
    
    // Procedures performed in Phase 3
    procedures: {
      abdominal: {
        operations: [
          'Remove packing',
          'Inspect for missed injuries',
          'Bowel anastomosis (reconnect bowel)',
          'Definitive vascular repair',
          'Fascial closure (close abdominal wall)',
          'Skin closure or VAC if contaminated'
        ],
        duration_minutes: 180,
        success_rate: 0.92
      },
      
      thoracic: {
        operations: [
          'Definitive vascular repair',
          'Chest wall reconstruction',
          'Diaphragm repair',
          'Definitive chest closure'
        ],
        duration_minutes: 150,
        success_rate: 0.88
      },
      
      orthopedic: {
        operations: [
          'Remove external fixation',
          'Internal fixation (plates, rods)',
          'Soft tissue coverage',
          'Amputation revision if needed'
        ],
        duration_minutes: 120,
        success_rate: 0.85
      }
    },
    
    // Resource consumption for Phase 3
    resources_consumed: {
      blood_products: {
        prbc_units: 4,               // Much less than Phase 1
        ffp_units: 2,
        total_units: 6,              // Minimal transfusion if resuscitated
        note: 'If bleeding during Phase 3, resuscitation inadequate'
      },
      
      operating_time: 3,             // 3 hours average
      
      team_requirements: {
        surgeon: 1,                  // Same skill as Phase 1
        assistant: 1,
        anesthesiologist: 1,
        scrub_tech: 1,
        circulating_nurse: 1
      }
    },
    
    // Facility requirements
    facility_capabilities: {
      role2_enhanced_cannot_perform: true,   // Beyond Role 2E capability
      role3_can_perform: true,               // Role 3 has capability
      role4_optimal: true,                   // Role 4 best for complex cases
      
      note: 'Some patients evacuated to Role 4 before Phase 3 to free Role 3 beds'
    },
    
    // Outcomes from Phase 3
    outcomes: {
      survival_to_discharge: 0.88,           // 88% survive Phase 3
      mortality_phase3: 0.12,                // 12% die during/after
      
      complications: {
        wound_infection: 0.35,               // 35% develop infection
        abdominal_compartment_syndrome: 0.08, // 8% need reopening
        anastomotic_leak: 0.12,              // 12% bowel leak
        re_operation_required: 0.22          // 22% need another surgery
      },
      
      long_term_outcomes: {
        full_recovery: 0.65,                 // 65% full recovery
        permanent_disability: 0.23,          // 23% permanent disability
        died: 0.12                          // 12% eventual mortality
      }
    }
  }
};

// ============================================================================
// PROLONGED FIELD CARE (PFC) - The Ukrainian Reality
// ============================================================================

/**
 * PFC protocols for when evacuation delayed beyond golden hour
 * 
 * EDUCATIONAL NOTE:
 * Traditional doctrine assumed golden hour ALWAYS achievable. Ukraine 2022-2024
 * proved this wrong. In contested environments, evacuation often delayed 4-12
 * hours. PFC is the bridge: keep patient alive until evacuation possible.
 * 
 * This is NOT failure - it's planned capability for modern warfare.
 * 
 * ROMANIAN MILITARY CONTEXT:
 * Most likely scenario: CONTESTED environment (Eastern European peer conflict).
 * PFC capability ESSENTIAL, not optional. Every medical unit needs PFC training
 * and equipment. This changes from "rare exception" to "expected capability".
 */
export const PROLONGED_FIELD_CARE = {
  
  /**
   * PFC Definition and Timeline
   */
  definition: {
    description: 'Extended medical care (2-72h) by non-physician providers when evacuation delayed',
    timeline_min_hours: 2,           // Shorter than this = normal prehospital
    timeline_typical_hours: 8,       // Ukrainian median
    timeline_max_hours: 72,          // Some cases documented
    
    providers: {
      primary: 'Combat Medic (68W, Medical Sergeant)',
      preferred: 'Special Forces Medic, PJ, SOCM (advanced training)',
      optimal: 'Physician Assistant forward deployed',
      not_suitable: 'Basic first aid trained personnel'
    },
    
    training_required: {
      pfc_course_hours: 80,          // Specialized PFC course
      prerequisite: 'TCCC provider certified',
      skills: [
        'Advanced airway management',
        'Chest tube placement',
        'Cricothyrotomy',
        'Antibiotic selection',
        'Pain management (including ketamine)',
        'Ventilator management (if available)',
        'Damage control resuscitation principles',
        'Medical decision making under uncertainty'
      ]
    }
  },

  /**
   * When PFC Required - Evacuation Paradigms
   */
  indications: {
    contested_environment: {
      description: 'Enemy air defense, evacuation helicopters unavailable',
      frequency_ukraine: 0.45,       // 45% of casualties
      median_pfc_duration_hours: 8
    },
    
    denied_environment: {
      description: 'No air or ground evacuation possible',
      frequency_ukraine: 0.05,       // 5% of casualties (rare but happens)
      median_pfc_duration_hours: 24
    },
    
    weather_delay: {
      description: 'Severe weather grounds helicopters',
      frequency_ukraine: 0.15,       // 15% of casualties
      median_pfc_duration_hours: 12
    },
    
    mass_casualty: {
      description: 'Evacuation assets overwhelmed, must wait',
      frequency_ukraine: 0.08,       // 8% of casualties
      median_pfc_duration_hours: 6
    },
    
    total_requiring_pfc: 0.35        // 35% of T1/T2 casualties require PFC
  },

  /**
   * PFC Interventions - What medics do during extended care
   */
  interventions: {
    airway_management: {
      basic: 'Nasopharyngeal airway, positioning',
      advanced: 'Supraglottic airway (iGel, King LT)',
      surgical: 'Cricothyrotomy if airway obstruction',
      monitoring: 'SpO2 continuous monitoring',
      equipment_weight_kg: 2
    },
    
    breathing_support: {
      oxygen: 'Supplemental O2 to maintain SpO2 >90%',
      chest_decompression: 'Needle decompression repeated as needed',
      chest_tube: 'Tube thoracostomy if trained and equipped',
      monitoring: 'Respiratory rate, SpO2, lung sounds Q15min',
      equipment_weight_kg: 3
    },
    
    circulation_support: {
      hemorrhage_control: 'Tourniquet maintenance, wound packing, hemostatic agents',
      fluid_resuscitation: '1L boluses (max 2L) - avoid over-resuscitation',
      blood_products: 'Whole blood if walking blood bank available',
      txa: 'Tranexamic acid if <3h from injury',
      permissive_hypotension: 'Target SBP 80-90 mmHg if conscious',
      monitoring: 'HR, BP, capillary refill Q15min',
      equipment_weight_kg: 5
    },
    
    pain_management: {
      mild_pain: 'Oral acetaminophen, ibuprofen',
      moderate_pain: 'Oral morphine, oxycodone',
      severe_pain: 'IV/IM morphine, fentanyl',
      ketamine: 'Ketamine 20-30mg IM/IV for severe pain or procedures',
      nerve_blocks: 'Femoral/popliteal blocks if trained',
      equipment_weight_kg: 1
    },
    
    infection_prevention: {
      antibiotics: 'Oral cefazolin + metronidazole, OR moxifloxacin',
      wound_care: 'Dressing changes Q8-12h',
      irrigation: 'Sterile saline irrigation if contaminated',
      tetanus: 'Tetanus toxoid if available',
      equipment_weight_kg: 1
    },
    
    hypothermia_prevention: {
      methods: 'Hypothermia blanket, remove wet clothing, warm IV fluids',
      target: 'Temperature >35Â°C',
      monitoring: 'Core temperature Q2h',
      equipment_weight_kg: 2
    },
    
    documentation: {
      tccc_card: 'Maintain TCCC casualty card',
      mist_report: 'Periodic MIST reports to higher HQ',
      photos: 'Document wounds if able (medical-legal)',
      equipment_weight_kg: 0.5
    }
  },

  /**
   * PFC Equipment Requirements
   */
  equipment_sets: {
    basic_pfc_kit: {
      description: 'Minimum for 24h PFC',
      weight_kg: 8,
      volume_liters: 15,
      cost_usd: 800,
      
      contents: [
        'Airway: NPA x2, iGel x2, cricothyrotomy kit',
        'Breathing: Chest seals x4, decompression needles x4, O2 15L',
        'Circulation: Tourniquets x4, hemostatic gauze x6, IV catheters x6',
        'Fluids: Normal saline 1L x4, blood administration set',
        'Medications: Morphine, ketamine, TXA, antibiotics, antiemetics',
        'Monitoring: Pulse oximeter, BP cuff, thermometer',
        'Wound care: Gauze, dressings, irrigation, tape',
        'Documentation: TCCC cards, pens, camera'
      ]
    },
    
    advanced_pfc_kit: {
      description: 'Enhanced capability for 72h PFC',
      weight_kg: 18,
      volume_liters: 35,
      cost_usd: 2500,
      
      additional_items: [
        'Chest tube kit with Heimlich valve',
        'Surgical airway kit (scalpel, tubes, retractors)',
        'Blood products: 2 units PRBC, 2 units FFP (if cold chain)',
        'Advanced medications: Vasopressors, sedatives',
        'Ultrasound (portable) for FAST exam',
        'Suction device (manual)',
        'Splinting materials',
        'Additional fluids and medications for 72h'
      ]
    }
  },

  /**
   * PFC Outcomes - Does it work?
   */
  outcomes: {
    survival_with_pfc: 0.68,         // 68% survival when PFC provided
    survival_without_pfc: 0.31,      // 31% survival when delayed evacuation, no PFC
    survival_benefit: 0.37,          // 37% absolute risk reduction
    
    nnt: 3,                          // Number Needed to Treat = 3 (very effective)
    
    ukraine_data: {
      total_casualties_requiring_pfc: 0.35,      // 35% of T1/T2 casualties
      pfc_capable_medics_deployed: 0.42,         // Only 42% of medics PFC-trained
      pfc_equipment_available: 0.38,             // Only 38% had full PFC kits
      gap: 'Training and equipment not matching operational need'
    },
    
    complications_during_pfc: {
      infection: 0.28,               // 28% develop infection during PFC
      compartment_syndrome: 0.08,    // 8% develop compartment syndrome
      acute_kidney_injury: 0.12,     // 12% develop AKI
      deterioration_requiring_field_amputation: 0.02  // 2% emergency amputations
    }
  },

  /**
   * PFC Training Requirements for Romanian Army
   */
  romanian_implementation: {
    current_state: {
      tccc_trained_percentage: 0.60,      // 60% of medics TCCC-certified
      pfc_trained_percentage: 0.15,       // Only 15% PFC-certified
      equipment_availability: 0.25,       // 25% have PFC kits
      status: 'INADEQUATE FOR CONTESTED OPERATIONS'
    },
    
    required_state: {
      tccc_trained_target: 0.95,          // 95% TCCC-certified
      pfc_trained_target: 0.50,           // 50% PFC-certified (realistic)
      equipment_availability_target: 0.50, // 50% equipped (budget constraint)
      timeline_years: 2
    },
    
    training_plan: {
      tccc_course_duration_days: 5,
      pfc_course_duration_days: 10,
      refresher_training_months: 12,      // Annual refresher
      live_tissue_training: 'Required for surgical airways, chest tubes',
      cost_per_medic_usd: 3500
    }
  }
};

// ============================================================================
// FACILITY SURGICAL CAPABILITIES
// ============================================================================

/**
 * Role-level surgical capabilities for DCS planning
 * 
 * CRITICAL FOR STUDENTS:
 * Role 2 Enhanced CAN do Phase 1 DCS (abbreviated surgery).
 * Role 3 CAN do all three phases (complete DCS cycle).
 * Understanding these capabilities drives medical planning.
 */
export const FACILITY_CAPABILITIES = {
  
  ROLE_1_BATTALION: {
    surgical_capability: false,
    dcs_capability: 'NONE',
    can_provide_pfc: true,           // PFC is Role 1 capability!
    max_hold_time_hours: 2,
    
    capabilities: [
      'Triage',
      'TCCC interventions',
      'Prolonged Field Care',
      'Stabilization for evacuation',
      'Return to Duty for T3 casualties'
    ]
  },

  ROLE_2_BASIC_BRIGADE: {
    surgical_capability: false,
    dcs_capability: 'NONE',
    can_provide_pfc: true,
    max_hold_time_hours: 12,
    
    capabilities: [
      'Advanced resuscitation',
      'Damage control resuscitation',
      'Blood transfusion',
      'PFC with physician oversight',
      'Hold T2 casualties 12-24h'
    ]
  },

  ROLE_2_ENHANCED_DIVISION: {
    surgical_capability: true,
    dcs_capability: 'PHASE_1_ONLY',  // CAN do abbreviated surgery
    blood_bank_required: true,
    max_hold_time_hours: 24,
    
    surgical_capacity: {
      teams: 1,                      // One surgical team
      surgeries_per_24h: 6,          // 6 major surgeries max
      phase1_dcs_per_24h: 8,         // Can do 8 DCS (faster than definitive)
      limitation: 'Cannot do Phase 3 definitive repair'
    },
    
    capabilities: [
      'Phase 1 DCS (abbreviated surgery)',
      'Phase 2 resuscitation (limited duration)',
      'Damage control resuscitation',
      'Full blood bank',
      'Cannot do Phase 3 (must evacuate to Role 3)'
    ]
  },

  ROLE_3_CORPS: {
    surgical_capability: true,
    dcs_capability: 'ALL_PHASES',    // Full DCS capability
    icu_capability: true,
    max_hold_time_days: 7,
    
    surgical_capacity: {
      teams: 4,                      // Four surgical teams
      surgeries_per_24h: 24,         // 24 major surgeries
      icu_beds: 12,                  // 12 ICU beds
      ward_beds: 48                  // 48 ward beds
    },
    
    capabilities: [
      'Phase 1 DCS',
      'Phase 2 ICU resuscitation (full duration)',
      'Phase 3 definitive surgery',
      'Specialty surgery (neurosurgery, vascular, orthopedic)',
      'Extended patient hold (7+ days)',
      'Can complete entire DCS cycle'
    ]
  },

  ROLE_4_STRATEGIC: {
    surgical_capability: true,
    dcs_capability: 'ALL_PHASES_PLUS',
    icu_capability: true,
    max_hold_time_days: 90,
    
    capabilities: [
      'All DCS phases',
      'Reconstructive surgery',
      'Long-term ICU',
      'Rehabilitation',
      'Specialty care (all subspecialties)'
    ]
  }
};

// ============================================================================
// REBOA PROTOCOL (Resuscitative Endovascular Balloon Occlusion of Aorta)
// ============================================================================

/**
 * REBOA - Advanced hemorrhage control technique
 * Balloon occlusion of aorta to control non-compressible torso hemorrhage
 */
export const REBOA_PROTOCOL = {
  name: 'Resuscitative Endovascular Balloon Occlusion of the Aorta',
  
  description: 'Temporary aortic occlusion using endovascular balloon to control ' +
               'non-compressible hemorrhage below the occlusion point',
  
  zones: {
    ZONE_1: {
      location: 'Descending thoracic aorta (above celiac artery)',
      indication: 'Abdominal and pelvic hemorrhage',
      placement: 'Left subclavian to celiac axis',
      max_occlusion_minutes: 60,
      notes: 'Most common placement for junctional hemorrhage'
    },
    ZONE_2: {
      location: 'Paravisceral aorta (celiac to renal arteries)',
      indication: 'AVOID - visceral ischemia',
      notes: 'Do NOT occlude in Zone 2'
    },
    ZONE_3: {
      location: 'Infrarenal aorta (below renal arteries)',
      indication: 'Pelvic and junctional hemorrhage',
      max_occlusion_minutes: 90,
      notes: 'Longer occlusion tolerated than Zone 1'
    }
  },
  
  indications: [
    'Non-compressible torso hemorrhage (NCTH)',
    'Junctional hemorrhage (groin, axilla)',
    'Pelvic fracture with hemorrhagic shock',
    'Traumatic abdominal hemorrhage pre-OR',
    'Bridge to OR when surgery delayed'
  ],
  
  contraindications: [
    'Thoracic aortic injury',
    'Injury above occlusion zone',
    'Traumatic arrest >10 minutes',
    'Known aortic disease'
  ],
  
  equipment: {
    access: 'Femoral artery (7-12 Fr sheath)',
    catheter: 'REBOA catheter (ER-REBOA, Prytime)',
    imaging: 'Fluoroscopy optimal, external landmarks acceptable',
    cost_usd: 500  // Per catheter
  },
  
  outcomes: {
    survival_improvement: 0.15,  // 15% absolute improvement
    bridge_to_surgery_success: 0.85,
    complications: {
      limb_ischemia: 0.08,
      aortic_injury: 0.02,
      failed_access: 0.10
    }
  },
  
  role_level: {
    role_1: false,
    role_2_basic: false,
    role_2_enhanced: true,  // Can be performed at R2E
    role_3: true
  },
  
  training_required: {
    course_hours: 16,
    prerequisite: 'Central line certification',
    simulation: 'Required before live use',
    annual_recertification: true
  }
};

// ============================================================================
// LETHAL TRIAD ALGORITHM
// ============================================================================

/**
 * Lethal Triad Management Algorithm
 * Hypothermia + Acidosis + Coagulopathy = Death spiral
 */
export const LETHAL_TRIAD_ALGORITHM = {
  name: 'Lethal Triad (Trauma Triad of Death)',
  
  components: {
    HYPOTHERMIA: {
      definition: 'Core temperature <35°C',
      thresholds: {
        mild: { temp_c: 35, mortality_increase: 1.2 },
        moderate: { temp_c: 34, mortality_increase: 1.8 },
        severe: { temp_c: 32, mortality_increase: 3.0 }
      },
      mechanism: 'Impairs coagulation enzyme function, reduces platelet activity',
      correction: {
        methods: ['Forced air warming', 'Warm IV fluids', 'Warm blood products', 
                  'Peritoneal lavage', 'ECMO rewarming'],
        target: '>36°C',
        rate: '1-2°C per hour'
      }
    },
    
    ACIDOSIS: {
      definition: 'pH <7.35 or lactate >4 mmol/L',
      thresholds: {
        mild: { ph: 7.30, mortality_increase: 1.3 },
        moderate: { ph: 7.20, mortality_increase: 2.0 },
        severe: { ph: 7.10, mortality_increase: 4.0 }
      },
      mechanism: 'Impairs clotting factor function, reduces enzyme activity',
      correction: {
        methods: ['Restore tissue perfusion', 'Optimize ventilation', 
                  'Correct underlying cause', 'Bicarbonate if pH <7.1'],
        target: 'pH >7.35, lactate <2',
        warning: 'Do NOT give bicarbonate unless pH <7.1 AND ventilation adequate'
      }
    },
    
    COAGULOPATHY: {
      definition: 'INR >1.5 or clinical bleeding',
      thresholds: {
        mild: { inr: 1.5, mortality_increase: 1.5 },
        moderate: { inr: 2.0, mortality_increase: 2.5 },
        severe: { inr: 2.5, mortality_increase: 4.0 }
      },
      mechanism: 'Dilution, consumption, dysfunction of clotting factors',
      correction: {
        methods: ['FFP transfusion', 'Platelet transfusion', 'Cryoprecipitate', 
                  'TXA', 'Factor VIIa (last resort)'],
        target: 'INR <1.2, platelets >100k, fibrinogen >150',
        ratio: '1:1:1 PRBC:FFP:Platelets'
      }
    }
  },
  
  // Scoring system for lethal triad severity
  scoring: {
    hypothermia: {
      '>36': 0, '35-36': 1, '34-35': 2, '32-34': 3, '<32': 4
    },
    ph: {
      '>7.35': 0, '7.25-7.35': 1, '7.20-7.25': 2, '7.10-7.20': 3, '<7.10': 4
    },
    inr: {
      '<1.2': 0, '1.2-1.5': 1, '1.5-2.0': 2, '2.0-2.5': 3, '>2.5': 4
    },
    
    interpretation: {
      '0-2': { risk: 'LOW', action: 'Standard care' },
      '3-5': { risk: 'MODERATE', action: 'Aggressive resuscitation, consider DCS' },
      '6-8': { risk: 'HIGH', action: 'DCS mandatory, ICU resuscitation critical' },
      '9-12': { risk: 'CRITICAL', action: 'DCS, expect high mortality' }
    }
  },
  
  synergy_effect: {
    description: 'Each component worsens the others in a death spiral',
    mortality_combined: 0.85,  // 85% mortality with all three
    mortality_single: 0.25,   // 25% mortality with just one
    time_to_death_hours: 4    // Death within 4 hours if uncorrected
  }
};

// ============================================================================
// DCS INDICATION SCORING
// ============================================================================

/**
 * DCS Indication Score - Decision support for DCS vs definitive surgery
 */
export const DCS_INDICATION_SCORE = {
  name: 'Damage Control Surgery Indication Score',
  
  criteria: {
    hemodynamic: {
      sbp_below_90: { points: 2, description: 'SBP <90 despite resuscitation' },
      requiring_pressors: { points: 2, description: 'On vasopressors pre-OR' },
      cardiac_arrest: { points: 3, description: 'ED thoracotomy/CPR' }
    },
    
    metabolic: {
      temp_below_35: { points: 2, description: 'Temperature <35°C' },
      ph_below_7_2: { points: 2, description: 'pH <7.2' },
      base_deficit_over_10: { points: 1, description: 'Base deficit >10' },
      lactate_over_5: { points: 1, description: 'Lactate >5 mmol/L' }
    },
    
    coagulation: {
      clinical_coagulopathy: { points: 2, description: 'Clinical bleeding' },
      inr_over_1_5: { points: 1, description: 'INR >1.5' },
      massive_transfusion: { points: 2, description: '>10 units PRBC' }
    },
    
    anatomic: {
      multi_cavity: { points: 2, description: 'Thoracic + abdominal injuries' },
      major_vascular: { points: 2, description: 'Major vascular injury' },
      complex_liver: { points: 1, description: 'Complex liver injury' },
      pancreatic_duodenal: { points: 1, description: 'Pancreatoduodenal injury' }
    },
    
    operational: {
      many_casualties_waiting: { points: 2, description: 'MASCAL situation' },
      limited_blood_supply: { points: 1, description: 'Blood stocks critical' },
      surgeon_fatigue: { points: 1, description: 'Surgeon fatigued' }
    }
  },
  
  interpretation: {
    '0-3': { recommendation: 'DEFINITIVE_SURGERY', description: 'Proceed with definitive repair' },
    '4-6': { recommendation: 'CONSIDER_DCS', description: 'DCS may be beneficial' },
    '7-10': { recommendation: 'DCS_RECOMMENDED', description: 'Strong indication for DCS' },
    '>10': { recommendation: 'DCS_MANDATORY', description: 'DCS is essential for survival' }
  }
};

// ============================================================================
// TEMPERATURE MANAGEMENT PROTOCOLS
// ============================================================================

/**
 * Temperature management in trauma
 */
export const TEMPERATURE_MANAGEMENT = {
  importance: 'Hypothermia is the most reversible component of the lethal triad',
  
  targets: {
    prehospital: '>35°C (prevent further cooling)',
    phase_1_surgery: '>35°C during surgery',
    phase_2_icu: '>36°C (active rewarming)',
    phase_3_surgery: '>36.5°C (normothermic)'
  },
  
  methods: {
    PASSIVE: {
      description: 'Prevent heat loss',
      interventions: ['Remove wet clothing', 'Warm blankets', 'Increase room temp'],
      rate_c_per_hour: 0.5,
      appropriate_for: 'Mild hypothermia >35°C'
    },
    
    ACTIVE_EXTERNAL: {
      description: 'External heat application',
      interventions: ['Forced air warming (Bair Hugger)', 'Heating blankets', 
                      'Radiant warmers', 'Warm packs to axillae/groin'],
      rate_c_per_hour: 1.5,
      appropriate_for: 'Moderate hypothermia 32-35°C'
    },
    
    ACTIVE_INTERNAL: {
      description: 'Core rewarming',
      interventions: ['Warm IV fluids (40°C)', 'Warm blood products', 
                      'Peritoneal lavage', 'Pleural lavage', 'Bladder irrigation'],
      rate_c_per_hour: 3.0,
      appropriate_for: 'Severe hypothermia <32°C'
    },
    
    ECMO: {
      description: 'Extracorporeal rewarming',
      interventions: ['VA-ECMO', 'Cardiopulmonary bypass'],
      rate_c_per_hour: 5.0,
      appropriate_for: 'Cardiac arrest from hypothermia, T <28°C',
      availability: 'Role 3/4 only'
    }
  },
  
  fluid_warming: {
    target_temp_c: 40,
    warming_devices: ['Level 1 rapid infuser', 'Belmont FMS', 'enFlow'],
    cold_crystalloid_effect: '-0.25°C per liter cold fluid'
  },
  
  or_temperature: {
    room_temp_c: 25,
    notes: 'Surgeons uncomfortable but patient survives'
  }
};

// ============================================================================
// UKRAINE SURGICAL STATISTICS (2022-2024)
// ============================================================================

/**
 * DCS and surgical statistics from Ukraine conflict
 */
export const UKRAINE_SURGICAL_STATISTICS = {
  period: '2022-2024',
  
  dcs_usage: {
    percentage_of_surgical_cases: 0.65,  // 65% of surgeries were DCS
    percentage_in_2022: 0.45,
    percentage_in_2024: 0.72,
    trend: 'INCREASING',
    reason: 'Surgeons learned DCS saves lives in high-volume situations'
  },
  
  timing: {
    median_injury_to_surgery_minutes: 180,
    role_2_surgery_percentage: 0.55,
    role_3_surgery_percentage: 0.45,
    golden_hour_achievement: 0.38
  },
  
  outcomes: {
    overall_surgical_survival: 0.82,
    dcs_survival: 0.78,
    definitive_survival: 0.88,
    phase_1_to_phase_3_survival: 0.71
  },
  
  blood_usage: {
    mean_units_per_dcs: 24,
    median_units_per_dcs: 18,
    massive_transfusion_rate: 0.45,
    whole_blood_usage_percentage: 0.35
  },
  
  complications: {
    wound_infection: 0.42,
    anastomotic_leak: 0.15,
    fascial_dehiscence: 0.12,
    abdominal_compartment_syndrome: 0.08,
    sepsis: 0.28,
    mortality_from_complications: 0.18
  },
  
  surgical_capacity_lessons: [
    'One surgical team can do 6-8 DCS per 24 hours vs 3-4 definitive',
    'DCS frees OR tables faster, treating more patients',
    'Forward surgical capability at Role 2E saved more lives than any other intervention',
    'Blood products, not surgical skill, became the limiting factor',
    'Walking blood bank essential when supply lines disrupted'
  ]
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate blood product consumption for DCS patient
 * 
 * @param {string} phase - 'PHASE_1', 'PHASE_2', or 'PHASE_3'
 * @param {boolean} massiveTransfusion - True if >10 units required
 * @returns {Object} Blood units by type
 * 
 * @example
 * const blood = calculateDCSBloodRequirements('PHASE_1', true);
 * // Returns: { prbc: 12, ffp: 12, platelets: 2, total: 26 }
 */
export function calculateDCSBloodRequirements(phase, massiveTransfusion = false) {
  const phases = DCS_PHASES;
  
  if (phase === 'PHASE_1') {
    if (massiveTransfusion) {
      return {
        prbc: 20,
        ffp: 20,
        platelets: 4,
        cryoprecipitate: 15,
        total: 59,
        warning: 'MASSIVE_TRANSFUSION_PROTOCOL'
      };
    } else {
      const p1 = phases.PHASE_1_ABBREVIATED_SURGERY.resources_consumed.blood_products;
      return {
        prbc: p1.prbc_units_typical,
        ffp: p1.ffp_units_typical,
        platelets: p1.platelet_units_typical,
        cryoprecipitate: p1.cryoprecipitate,
        total: p1.total_units_typical
      };
    }
  } else if (phase === 'PHASE_2') {
    const p2 = phases.PHASE_2_ICU_RESUSCITATION.resources_consumed.blood_products_additional;
    return {
      prbc: p2.prbc_units,
      ffp: p2.ffp_units,
      platelets: p2.platelet_units,
      cryoprecipitate: p2.cryoprecipitate,
      total: p2.total_additional_units
    };
  } else if (phase === 'PHASE_3') {
    const p3 = phases.PHASE_3_DEFINITIVE_SURGERY.resources_consumed.blood_products;
    return {
      prbc: p3.prbc_units,
      ffp: p3.ffp_units,
      platelets: 0,
      total: p3.total_units
    };
  }
  
  return { error: 'Invalid phase' };
}

/**
 * Calculate lethal triad score
 * @param {number} temperature - Core temperature in Celsius
 * @param {number} ph - Blood pH
 * @param {number} inr - INR value
 * @returns {Object} Score and risk level
 */
export function calculateLethalTriadScore(temperature, ph, inr) {
  let tempScore = 0;
  if (temperature > 36) tempScore = 0;
  else if (temperature >= 35) tempScore = 1;
  else if (temperature >= 34) tempScore = 2;
  else if (temperature >= 32) tempScore = 3;
  else tempScore = 4;
  
  let phScore = 0;
  if (ph > 7.35) phScore = 0;
  else if (ph >= 7.25) phScore = 1;
  else if (ph >= 7.20) phScore = 2;
  else if (ph >= 7.10) phScore = 3;
  else phScore = 4;
  
  let inrScore = 0;
  if (inr < 1.2) inrScore = 0;
  else if (inr <= 1.5) inrScore = 1;
  else if (inr <= 2.0) inrScore = 2;
  else if (inr <= 2.5) inrScore = 3;
  else inrScore = 4;
  
  const totalScore = tempScore + phScore + inrScore;
  
  let risk, action;
  if (totalScore <= 2) {
    risk = 'LOW';
    action = 'Standard care';
  } else if (totalScore <= 5) {
    risk = 'MODERATE';
    action = 'Aggressive resuscitation, consider DCS';
  } else if (totalScore <= 8) {
    risk = 'HIGH';
    action = 'DCS mandatory, ICU resuscitation critical';
  } else {
    risk = 'CRITICAL';
    action = 'DCS, expect high mortality';
  }
  
  return {
    temperature_score: tempScore,
    ph_score: phScore,
    inr_score: inrScore,
    total_score: totalScore,
    risk_level: risk,
    recommended_action: action,
    mortality_estimate: totalScore <= 2 ? 0.10 : 
                        totalScore <= 5 ? 0.30 :
                        totalScore <= 8 ? 0.55 : 0.85
  };
}

/**
 * Calculate DCS indication score
 * @param {Object} criteria - Object with boolean flags for each criterion
 * @returns {Object} Score and recommendation
 */
export function calculateDCSIndicationScore(criteria) {
  let totalScore = 0;
  const triggeredCriteria = [];
  
  // Hemodynamic
  if (criteria.sbp_below_90) { totalScore += 2; triggeredCriteria.push('SBP <90'); }
  if (criteria.requiring_pressors) { totalScore += 2; triggeredCriteria.push('On pressors'); }
  if (criteria.cardiac_arrest) { totalScore += 3; triggeredCriteria.push('Cardiac arrest'); }
  
  // Metabolic
  if (criteria.temp_below_35) { totalScore += 2; triggeredCriteria.push('Temp <35°C'); }
  if (criteria.ph_below_7_2) { totalScore += 2; triggeredCriteria.push('pH <7.2'); }
  if (criteria.base_deficit_over_10) { totalScore += 1; triggeredCriteria.push('BD >10'); }
  if (criteria.lactate_over_5) { totalScore += 1; triggeredCriteria.push('Lactate >5'); }
  
  // Coagulation
  if (criteria.clinical_coagulopathy) { totalScore += 2; triggeredCriteria.push('Clinical coagulopathy'); }
  if (criteria.inr_over_1_5) { totalScore += 1; triggeredCriteria.push('INR >1.5'); }
  if (criteria.massive_transfusion) { totalScore += 2; triggeredCriteria.push('Massive transfusion'); }
  
  // Anatomic
  if (criteria.multi_cavity) { totalScore += 2; triggeredCriteria.push('Multi-cavity trauma'); }
  if (criteria.major_vascular) { totalScore += 2; triggeredCriteria.push('Major vascular injury'); }
  if (criteria.complex_liver) { totalScore += 1; triggeredCriteria.push('Complex liver injury'); }
  if (criteria.pancreatic_duodenal) { totalScore += 1; triggeredCriteria.push('Pancreatoduodenal injury'); }
  
  // Operational
  if (criteria.many_casualties_waiting) { totalScore += 2; triggeredCriteria.push('MASCAL'); }
  if (criteria.limited_blood_supply) { totalScore += 1; triggeredCriteria.push('Limited blood'); }
  if (criteria.surgeon_fatigue) { totalScore += 1; triggeredCriteria.push('Surgeon fatigue'); }
  
  let recommendation, description;
  if (totalScore <= 3) {
    recommendation = 'DEFINITIVE_SURGERY';
    description = 'Proceed with definitive repair';
  } else if (totalScore <= 6) {
    recommendation = 'CONSIDER_DCS';
    description = 'DCS may be beneficial';
  } else if (totalScore <= 10) {
    recommendation = 'DCS_RECOMMENDED';
    description = 'Strong indication for DCS';
  } else {
    recommendation = 'DCS_MANDATORY';
    description = 'DCS is essential for survival';
  }
  
  return {
    total_score: totalScore,
    triggered_criteria: triggeredCriteria,
    recommendation: recommendation,
    description: description
  };
}

/**
 * Get rewarming protocol based on temperature
 * @param {number} temperature - Core temperature in Celsius
 * @returns {Object} Recommended rewarming protocol
 */
export function getRewarmingProtocol(temperature) {
  const protocols = TEMPERATURE_MANAGEMENT.methods;
  
  if (temperature >= 35) {
    return {
      severity: 'MILD',
      protocol: 'PASSIVE',
      methods: protocols.PASSIVE.interventions,
      expected_rate: protocols.PASSIVE.rate_c_per_hour,
      time_to_normothermia_hours: (36 - temperature) / protocols.PASSIVE.rate_c_per_hour
    };
  } else if (temperature >= 32) {
    return {
      severity: 'MODERATE',
      protocol: 'ACTIVE_EXTERNAL',
      methods: protocols.ACTIVE_EXTERNAL.interventions,
      expected_rate: protocols.ACTIVE_EXTERNAL.rate_c_per_hour,
      time_to_normothermia_hours: (36 - temperature) / protocols.ACTIVE_EXTERNAL.rate_c_per_hour
    };
  } else if (temperature >= 28) {
    return {
      severity: 'SEVERE',
      protocol: 'ACTIVE_INTERNAL',
      methods: protocols.ACTIVE_INTERNAL.interventions,
      expected_rate: protocols.ACTIVE_INTERNAL.rate_c_per_hour,
      time_to_normothermia_hours: (36 - temperature) / protocols.ACTIVE_INTERNAL.rate_c_per_hour
    };
  } else {
    return {
      severity: 'PROFOUND',
      protocol: 'ECMO',
      methods: protocols.ECMO.interventions,
      expected_rate: protocols.ECMO.rate_c_per_hour,
      time_to_normothermia_hours: (36 - temperature) / protocols.ECMO.rate_c_per_hour,
      warning: 'ECMO required - Role 3/4 only'
    };
  }
}

/**
 * Calculate total DCS timeline for patient
 * 
 * @param {boolean} complications - True if patient has complications
 * @returns {Object} Timeline in hours and days
 * 
 * @example
 * const timeline = calculateDCSTimeline(false);
 * // Returns: { phase1_hours: 1, phase2_hours: 48, phase3_hours: 3, total_days: 3 }
 */
export function calculateDCSTimeline(complications = false) {
  const phases = DCS_PHASES;
  
  const phase1_hours = phases.PHASE_1_ABBREVIATED_SURGERY.duration_minutes_typical / 60;
  const phase2_hours = complications ? 
    phases.PHASE_2_ICU_RESUSCITATION.duration_hours_max :
    phases.PHASE_2_ICU_RESUSCITATION.duration_hours_typical;
  const phase3_hours = phases.PHASE_3_DEFINITIVE_SURGERY.duration_minutes_typical / 60;
  
  const total_hours = phase1_hours + phase2_hours + phase3_hours;
  const total_days = Math.ceil(total_hours / 24);
  
  return {
    phase1_hours: phase1_hours,
    phase2_hours: phase2_hours,
    phase3_hours: phase3_hours,
    total_hours: total_hours,
    total_days: total_days,
    discharge_day: total_days + 2  // 2 additional days recovery
  };
}

/**
 * Determine if PFC required based on operational conditions
 * 
 * @param {string} evacuationParadigm - 'PERMISSIVE', 'CONTESTED', 'DENIED'
 * @param {number} weatherSeverity - 0 (clear) to 3 (extreme)
 * @returns {Object} PFC requirement and probability
 * 
 * @example
 * const pfc = determinePFCRequirement('CONTESTED', 2);
 * // Returns: { required: true, probability: 0.58, median_duration_hours: 10 }
 */
export function determinePFCRequirement(evacuationParadigm, weatherSeverity = 0) {
  const pfc = PROLONGED_FIELD_CARE;
  
  let base_probability = 0;
  let median_duration = 0;
  
  if (evacuationParadigm === 'PERMISSIVE') {
    base_probability = 0.05;
    median_duration = 3;
  } else if (evacuationParadigm === 'CONTESTED') {
    base_probability = pfc.indications.contested_environment.frequency_ukraine;
    median_duration = pfc.indications.contested_environment.median_pfc_duration_hours;
  } else if (evacuationParadigm === 'DENIED') {
    base_probability = pfc.indications.denied_environment.frequency_ukraine;
    median_duration = pfc.indications.denied_environment.median_pfc_duration_hours;
  }
  
  // Weather increases PFC probability
  const weather_modifier = weatherSeverity * 0.10;
  const final_probability = Math.min(base_probability + weather_modifier, 0.90);
  
  return {
    required: final_probability > 0.30,
    probability: final_probability,
    median_duration_hours: median_duration + (weatherSeverity * 2),
    recommendation: final_probability > 0.30 ? 
      'DEPLOY_PFC_CAPABLE_MEDICS' : 
      'STANDARD_EVACUATION_ADEQUATE'
  };
}

/**
 * Calculate survival improvement from PFC capability
 * 
 * @param {number} casualtiesDelayedEvacuation - Number with >2h evac time
 * @param {boolean} pfcCapable - True if medics PFC-trained and equipped
 * @returns {Object} Survival statistics and lives saved
 * 
 * @example
 * const survival = calculatePFCSurvivalBenefit(50, true);
 * // Returns: { survivors_with_pfc: 34, survivors_without: 16, lives_saved: 18 }
 */
export function calculatePFCSurvivalBenefit(casualtiesDelayedEvacuation, pfcCapable) {
  const pfc = PROLONGED_FIELD_CARE.outcomes;
  
  const survivors_with_pfc = Math.round(casualtiesDelayedEvacuation * pfc.survival_with_pfc);
  const survivors_without = Math.round(casualtiesDelayedEvacuation * pfc.survival_without_pfc);
  
  if (pfcCapable) {
    return {
      total_casualties: casualtiesDelayedEvacuation,
      survivors: survivors_with_pfc,
      deaths: casualtiesDelayedEvacuation - survivors_with_pfc,
      survival_rate: pfc.survival_with_pfc,
      lives_saved_vs_no_pfc: survivors_with_pfc - survivors_without
    };
  } else {
    return {
      total_casualties: casualtiesDelayedEvacuation,
      survivors: survivors_without,
      deaths: casualtiesDelayedEvacuation - survivors_without,
      survival_rate: pfc.survival_without_pfc,
      lives_lost_vs_pfc: survivors_with_pfc - survivors_without,
      warning: 'PFC_CAPABILITY_CRITICAL'
    };
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

const damageControlSurgeryExports = {
  DCS_PHASES,
  PROLONGED_FIELD_CARE,
  FACILITY_CAPABILITIES,
  REBOA_PROTOCOL,
  LETHAL_TRIAD_ALGORITHM,
  DCS_INDICATION_SCORE,
  TEMPERATURE_MANAGEMENT,
  UKRAINE_SURGICAL_STATISTICS,
  calculateDCSBloodRequirements,
  calculateDCSTimeline,
  determinePFCRequirement,
  calculatePFCSurvivalBenefit,
  calculateLethalTriadScore,
  calculateDCSIndicationScore,
  getRewarmingProtocol
};

export default damageControlSurgeryExports;
